/* Interview Clozo — shared account/auth helpers.
   Exposes window.InterviewAuth. All endpoints live same-origin under /api
   (mirrors checkout.js). The session is persisted in localStorage under
   "interview_session" as { token, user, expiresAtMillis }.
   Passwords are only ever read from inputs and sent in the request body —
   they are never stored, logged, or persisted. */
(function () {
  'use strict';

  var SESSION_KEY = 'interview_session';

  /* API is served same-origin under /api (Caddy proxies to the billing
     service). Kept as a function to mirror checkout.js and to make a future
     path-prefix override trivial. */
  function apiBase() { return ''; }
  function apiUrl(path) {
    if (path.charAt(0) !== '/') path = '/' + path;
    return apiBase() + path;
  }

  /* ---------------- session storage ---------------- */
  function getSession() {
    try {
      var raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      return (s && s.token) ? s : null;
    } catch (e) { return null; }
  }
  function rt() { return window.__clozoRuntime || null; }   // site-runtime.js (W6); every call guarded
  function setSession(s) {
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch (e) {}
    try { var r = rt(); if (r && r.setUser) r.setUser(s && s.user && s.user.id); } catch (e) {}   // Sentry: uuid only
  }
  function clearSession() {
    try { localStorage.removeItem(SESSION_KEY); } catch (e) {}
    try { var r = rt(); if (r && r.setUser) r.setUser(null); } catch (e) {}
  }
  /* OBSERVABILITY §2.7: the one choke point of every /api call on the auth, account, success, welcome and app-signin
     pages. Machine values only (path, method, status, code); expected 4xx codes are skipped by the runtime. */
  var AREA = { account: 'account', success: 'success', welcome: 'welcome', 'app-signin': 'app_signin', install: 'install', cancel: 'cancel' };
  function failure(path, method, info) {
    try {
      var r = rt(); if (!r || !r.reportFailure) return;
      var page = String(location.pathname).replace(/^\//, '').replace(/\.html$/, '');
      info.path = path; info.method = method;
      r.reportFailure(AREA[page] || 'auth', info);
    } catch (e) {}
  }
  function isExpired(s) {
    if (!s) return true;
    if (typeof s.expiresAtMillis === 'number' && s.expiresAtMillis > 0) {
      return Date.now() >= s.expiresAtMillis;
    }
    return false; // no expiry info -> valid until the server rejects it
  }
  function token() {
    var s = getSession();
    return (s && !isExpired(s)) ? s.token : null;
  }
  function currentUser() {
    var s = getSession();
    return (s && !isExpired(s)) ? (s.user || null) : null;
  }

  /* ---------------- the first-party browser id (B21; CONTRACT §11.5, Δ22) ----------------
     localStorage.clozo_bid = "b1_" + 22 base64url chars (128 random bits), derived from nothing. Created only after the
     browser COMPLETES a sign-in (web login, R17, R19), so an anonymous visitor never gets one. Sent as X-Clozo-Browser on
     web /auth/login, R17, R19 and RB1 (book.js) — nothing else carries it. Functional, always on (not a cookie). */
  var BID_KEY = 'clozo_bid';
  function browserId() {
    try { var v = localStorage.getItem(BID_KEY); return /^b1_[A-Za-z0-9_-]{22}$/.test(v || '') ? v : null; } catch (e) { return null; }
  }
  function ensureBrowserId() {
    var v = browserId(); if (v) return v;
    try {
      var b = new Uint8Array(16); crypto.getRandomValues(b);
      var s = ''; for (var i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
      v = 'b1_' + btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      localStorage.setItem(BID_KEY, v);
      return browserId();
    } catch (e) { return null; }
  }

  /* ---------------- http ---------------- */
  function request(path, opts) {
    opts = opts || {};
    var headers = { 'Content-Type': 'application/json' };
    if (opts.browser) { var bid = browserId(); if (bid) headers['X-Clozo-Browser'] = bid; }   // B21: login, R17, R19, RB1 only
    if (opts.auth) {
      var t = token();
      if (t) headers['Authorization'] = 'Bearer ' + t;
    }
    var init = { method: opts.method || 'GET', headers: headers };
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
    return fetch(apiUrl(path), init).then(function (res) {
      var ct = res.headers.get('content-type') || '';
      var isJson = ct.indexOf('application/json') !== -1;
      var parse = isJson
        ? res.json().catch(function () { return {}; })
        : Promise.resolve({});
      return parse.then(function (data) {
        data = data || {};
        if (!res.ok) failure(path, init.method, { status: res.status, code: data.code || data.error, nonJson: !isJson });
        return { ok: res.ok, status: res.status, data: data };
      });
    }, function (err) {
      failure(path, init.method, { status: 0, network: true });
      throw err;                                           // callers behave exactly as before
    });
  }

  /* Resolve a successful response's body, or throw an Error carrying the
     server's { error, code } plus the HTTP status. */
  function unwrap(r, fallback) {
    if (r.ok) return r.data;
    var err = new Error((r.data && r.data.error) || fallback || 'Something went wrong. Please try again.');
    err.code = r.data && r.data.code;
    err.status = r.status;
    if (r.data && r.data.retryAfterSeconds) err.retryAfterSeconds = r.data.retryAfterSeconds;   // R081: the lock's minutes
    throw err;
  }

  /* ---------------- validation ---------------- */
  function validEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim()); }

  /* Best-effort, privacy-light device fingerprint for signup abuse controls.
     Non-identifying, coarse, and stable-ish across a session. */
  function deviceFingerprint() {
    try {
      var parts = [
        navigator.userAgent || '',
        navigator.language || '',
        (screen.width + 'x' + screen.height),
        screen.colorDepth || '',
        new Date().getTimezoneOffset()
      ];
      var str = parts.join('|');
      var h = 0;
      for (var i = 0; i < str.length; i++) { h = (h * 31 + str.charCodeAt(i)) | 0; }
      return 'web_' + (h >>> 0).toString(16);
    } catch (e) { return 'web_unknown'; }
  }

  /* ---------------- API methods ---------------- */
  /* No web sign-up (owner ruling D18): the website creates accounts only from a checkout
     (CONTRACT R4 / D13.8), so this file carries no /api/auth/signup helper. */
  function verify(tok) {
    // auth: the bearer (when this browser holds one) tells the server the click is the account's own browser, so a
    // Join account is not claimed from under its registrant (CONTRACT-API §3.7 (o)(i) path 1).
    return request('/api/auth/verify', { method: 'POST', auth: true, body: { token: tok } })
      .then(function (r) { return unwrap(r, 'This verification link is invalid or has expired.'); });
  }

  // Always resolves ok (no account enumeration).
  function resend(email) {
    return request('/api/auth/resend', { method: 'POST', body: { email: email } })
      .then(function (r) { return r.data; });
  }

  function login(username, password) {
    return request('/api/auth/login', { method: 'POST', browser: true, body: { username: username, password: password, surface: 'web' } })
      .then(function (r) {
        if (r.ok && r.data && r.data.token) {
          setSession({ token: r.data.token, user: r.data.user || null, expiresAtMillis: r.data.expiresAtMillis || 0 });
          ensureBrowserId();
          // setSession swallows storage exceptions; if persistence silently failed
          // (e.g. Safari "Block all cookies" makes localStorage throw), the caller
          // would navigate to /account.html and be bounced straight back to login
          // with no error. Probe getSession() (not token(), which also checks
          // expiry) and surface an actionable message instead.
          if (!getSession()) {
            throw new Error('Your browser is blocking site storage. Enable cookies/site data for interview.clozo.ai and try again.');
          }
          return r.data;
        }
        return unwrap(r, 'Could not sign you in. Please try again.');
      });
  }

  /* ---------------- sign-in without typing a password (W5; CONTRACT R17, R18, R19, R35) ----------------
     Every call resolves {ok, status, data} and never throws (a network failure is status 0), so the pages word each
     answer themselves. R17 / R19 are PUBLIC: the credential is the checkout nonce or the email-link token. */
  function adoptSession(data) {
    if (!data || !data.token) return false;
    setSession({ token: data.token, user: data.user || null, expiresAtMillis: data.expiresAtMillis || 0 });
    var ok = !!getSession();
    if (ok) ensureBrowserId();                             // B21: a completed sign-in (R17, R19)
    // The page stays open after R17 / R19: switch the navbar to the signed-in state now (scripts.js applyAuthNav).
    if (ok) { try { if (typeof window.__clozoAuthNav === 'function') window.__clozoAuthNav(); } catch (e) {} }
    return ok;
  }
  function safeCall(path, opts) {
    return request(path, opts).then(null, function () { return { ok: false, status: 0, data: {}, network: true }; });
  }
  /* Public sign-in posts (R17, R19). One place, so the book lane's browser id header (B21, X-Clozo-Browser) is added
     here once for both. */
  function publicSigninPost(path, body) {
    return safeCall(path, { method: 'POST', body: body, browser: true });
  }
  /* R17: the browser that started a signed-out checkout signs in to the account that checkout created.
     q = {session_id} (Stripe's checkout reference); nonce = the 64-hex text checkout.js stored (clozo_checkout_nonce). */
  function checkoutSignin(q, nonce) {
    var body = { nonce: nonce };
    if (q && q.session_id) body.session_id = q.session_id;
    return publicSigninPost('/api/public/checkout/signin', body).then(function (r) {
      if (r.ok) r.adopted = adoptSession(r.data);
      return r;
    });
  }
  /* R19: an email sign-in / action link (welcome.html). Called on a click, never on page load (scanner-safe).
     D-FS1-2: the page's own session bearer rides along when it has one, so a click in the SAME browser that set the
     password keeps that session and the password (the server never requires it). */
  function emailSignin(tok) {
    return safeCall('/api/auth/email-signin', { method: 'POST', body: { token: tok }, browser: true, auth: true }).then(function (r) {
      if (r.ok) r.adopted = adoptSession(r.data);
      return r;
    });
  }
  /* FS1 #25: correct the email typed on Stripe's page while the checkout grant is fresh (POST /public/checkout/email). */
  function checkoutEmailFix(q, email) {
    var body = { email: email };
    if (q && q.session_id) body.session_id = q.session_id;
    return safeCall('/api/public/checkout/email', { method: 'POST', body: body, auth: true });
  }
  /* FS1 #26: "Add this purchase" on account.html — redeem an activation code onto the signed-in account (POST /activate). */
  function activate(code) {
    return safeCall('/api/activate', { method: 'POST', body: { token: code }, auth: true });
  }
  /* R18: set the first password (a checkout / email-link session younger than 30 min) or change it. */
  function setPassword(password, currentPassword) {
    var body = { password: password };
    if (currentPassword) body.currentPassword = currentPassword;
    return safeCall('/api/auth/password', { method: 'POST', body: body, auth: true });
  }
  /* R35: a single-use 120-s code that signs the Mac app in (interview://signin#c=<code>). Minted on a click only. */
  function appLink() {
    return safeCall('/api/auth/app-link', { method: 'POST', body: {}, auth: true });
  }

  function me() {
    return request('/api/auth/me', { method: 'GET', auth: true })
      .then(function (r) { return unwrap(r, 'Could not load your account.'); });
  }

  // Entitlement: { plan:{planID,name}, status, quota, freeTrial }
  function entitlement() {
    return request('/api/me', { method: 'GET', auth: true })
      .then(function (r) { return unwrap(r, 'Could not load your plan.'); });
  }

  // Always resolves ok (no account enumeration).
  function requestReset(email) {
    return request('/api/auth/request-reset', { method: 'POST', body: { email: email } })
      .then(function (r) { return r.data; });
  }

  function reset(tok, password) {
    return request('/api/auth/reset', { method: 'POST', body: { token: tok, password: password } })
      .then(function (r) { return unwrap(r, 'Could not reset your password. The link may have expired.'); });
  }

  // Public (unauthenticated) portal: the server EMAILS the portal link to the address on file and
  // returns only { ok:true } (never a URL — that would let anyone open another person's billing portal).
  function portal(email) {
    return request('/api/public/portal', { method: 'POST', body: { email: email } })
      .then(function (r) { return r.data || { ok: true }; });
  }

  // Authenticated portal for the logged-in user: the account is derived from the session, so the URL is
  // safe to return and open directly (only ever this user's own portal).
  function authedPortal() {
    return request('/api/portal', { method: 'POST', body: {}, auth: true })
      .then(function (r) { return unwrap(r, 'Could not open the billing portal.'); });
  }

  function logout() {
    var t = token();
    var done = function () { clearSession(); return { ok: true }; };
    if (!t) return Promise.resolve(done());
    return request('/api/auth/logout', { method: 'POST', auth: true }).then(done, done);
  }

  /* Redirect to login if there is no valid session. Preserves the intended
     destination as ?next= so login can bounce the user back. Returns true
     when authenticated. */
  function requireAuth(loginPage) {
    if (token()) return true;
    // Keep the #hash too: an account-page action link (#start-plan, #resume, …) must survive the sign-in.
    var next = encodeURIComponent(location.pathname + location.search + location.hash);
    location.replace((loginPage || '/login.html') + '?next=' + next);
    return false;
  }

  /* Return a safe same-origin relative path from ?next=, else fallback. Resolve against our own origin
     and require the result to STAY on it — this rejects protocol-relative ("//evil.com"), absolute
     ("http://evil.com"), backslash, and control-character ("/\tevil.com" → browser-normalized to
     "//evil.com") open-redirect payloads that a lone char-pair regex lets through. */
  function safeNext(fallback) {
    var fb = fallback || '/account.html';
    try {
      var next = new URLSearchParams(location.search).get('next');
      if (!next || next.charAt(0) !== '/' || /[\x00-\x1f\x7f\\ ]/.test(next)) return fb;
      var u = new URL(next, location.origin);
      if (u.origin !== location.origin) return fb;
      return u.pathname + u.search + u.hash;
    } catch (e) { return fb; }
  }

  /* Small UI convenience: wire any [data-pw-toggle="inputId"] show/hide button. */
  function initPasswordToggles() {
    var btns = document.querySelectorAll('[data-pw-toggle]');
    for (var i = 0; i < btns.length; i++) {
      (function (btn) {
        if (btn.dataset.pwBound) return;
        btn.dataset.pwBound = '1';
        btn.addEventListener('click', function () {
          var input = document.getElementById(btn.getAttribute('data-pw-toggle'));
          if (!input) return;
          var reveal = input.type === 'password';
          input.type = reveal ? 'text' : 'password';
          btn.textContent = reveal ? 'Hide' : 'Show';
          btn.setAttribute('aria-label', reveal ? 'Hide password' : 'Show password');
          btn.setAttribute('aria-pressed', reveal ? 'true' : 'false');
        });
      })(btns[i]);
    }
  }

  window.InterviewAuth = {
    SESSION_KEY: SESSION_KEY,
    getSession: getSession,
    setSession: setSession,
    clearSession: clearSession,
    token: token,
    currentUser: currentUser,
    validEmail: validEmail,
    deviceFingerprint: deviceFingerprint,
    verify: verify,
    resend: resend,
    login: login,
    me: me,
    entitlement: entitlement,
    requestReset: requestReset,
    reset: reset,
    portal: portal,
    authedPortal: authedPortal,
    logout: logout,
    requireAuth: requireAuth,
    safeNext: safeNext,
    initPasswordToggles: initPasswordToggles,
    adoptSession: adoptSession,
    checkoutSignin: checkoutSignin,
    checkoutEmailFix: checkoutEmailFix,
    activate: activate,
    emailSignin: emailSignin,
    setPassword: setPassword,
    appLink: appLink,
    browserId: browserId,
    ensureBrowserId: ensureBrowserId,
    safeCall: safeCall
  };
})();
